// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'
import { requestPasswordReset } from '../api/client'

interface Props { onScreenEnter: () => void }

export default function ForgotPasswordPage({ onScreenEnter }: Props) {
  useEffect(() => { onScreenEnter() }, [onScreenEnter])

  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (submitting) return
    setError(null)
    if (!email.trim()) { setError('Please enter the email for your account.'); return }

    setSubmitting(true)
    try {
      await requestPasswordReset(email.trim())
      setDone(true)
    } catch (e: any) {
      setError(e?.body?.message || 'Could not submit request. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (done) {
    return (
      <div style={containerStyle}>
        <div style={successStyle}>
          <CheckCircle2 size={20} aria-hidden="true" style={{ flexShrink: 0 }} />
          <div>
            <strong>Check your email.</strong>
            <p style={{ margin: '6px 0 0', fontSize: 13, lineHeight: 1.5 }}>
              If an account exists for <code>{email.trim()}</code>, we’ve sent
              a reset link. The link is valid for 60 minutes.
            </p>
          </div>
        </div>
        <div style={altLinksStyle}>
          <Link to="/login" style={linkStyle}>Back to sign in</Link>
        </div>
      </div>
    )
  }

  return (
    <div style={containerStyle}>
      <h1 style={titleStyle}>Forgot your password?</h1>
      <p style={subtitleStyle}>
        Enter the email address associated with your account and we’ll send
        you a link to set a new password.
      </p>

      <form onSubmit={handleSubmit} style={formStyle} noValidate>
        <label style={labelStyle} htmlFor="reset-email">Email</label>
        <input
          id="reset-email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={e => setEmail(e.target.value)}
          disabled={submitting}
          required
          style={inputStyle}
        />

        {error && (
          <div role="alert" style={errorStyle}>
            <AlertCircle size={16} aria-hidden="true" style={{ flexShrink: 0 }} />
            <span>{error}</span>
          </div>
        )}

        <button type="submit" disabled={submitting} style={submitStyle}>
          {submitting ? (<><Loader2 size={16} className="spin" aria-hidden="true" /> Sending…</>) : 'Send reset link'}
        </button>
      </form>

      <div style={altLinksStyle}>
        <Link to="/login" style={linkStyle}>Back to sign in</Link>
      </div>
    </div>
  )
}

const containerStyle: React.CSSProperties = { maxWidth: 440, margin: '0 auto', padding: '32px 24px' }
const titleStyle: React.CSSProperties = {
  fontFamily: 'Georgia, serif',
  fontSize: 28, fontWeight: 700, margin: '0 0 8px', letterSpacing: -0.5,
}
const subtitleStyle: React.CSSProperties = {
  margin: '0 0 24px', color: 'var(--color-text-soft)', fontSize: 14, lineHeight: 1.5,
}
const formStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6 }
const labelStyle: React.CSSProperties = { fontSize: 13, fontWeight: 500, marginTop: 4 }
const inputStyle: React.CSSProperties = {
  padding: '10px 12px', border: '1px solid var(--color-border)',
  borderRadius: 6, fontSize: 14, fontFamily: 'inherit', background: 'var(--color-surface)',
}
const errorStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  marginTop: 12, padding: '8px 12px',
  background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 4,
  color: '#991b1b', fontSize: 13,
}
const successStyle: React.CSSProperties = {
  display: 'flex', gap: 12, padding: '14px 16px',
  background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 6,
  color: '#065f46', fontSize: 14,
}
const submitStyle: React.CSSProperties = {
  marginTop: 18,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  padding: '12px 18px',
  background: 'var(--color-primary, #047857)', color: '#fff',
  border: 'none', borderRadius: 6,
  fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
}
const altLinksStyle: React.CSSProperties = {
  display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'center',
  marginTop: 24, fontSize: 13,
}
const linkStyle: React.CSSProperties = {
  color: 'var(--color-primary, #047857)', textDecoration: 'none',
}
